import React, {useEffect, useRef, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import useApiDetail from "./apis/useApiDetail";
import BiographyBookMainData from "./BiographyBookMainData";
import BiographyViewPopup from "./BiographyViewPopup";
import BiographyEmpty from "./components/SearchEmpty";

const BiographyId = () => {
    const limit = 6;
    const {book_id} = useParams();
    const [isLoading, setIsLoading] = useState(false);
    const [filteredDatas, setFilteredDatas] = useState([]);
    const observerRef = useRef(null);
    const [hasNextPage, setHasNextPage] = useState(true);
    const index = useRef(0);
    const navigate = useNavigate();
    const [propShowModal, setPropShowModal] = useState(false);
    const [tocId, setTocId] = useState(null);
    const [imgItem, setImgItem] = useState('');
    const [isTocs, setIsTocs] = useState(false);

    const data = useApiDetail(book_id);
    const handleOpenModal = () => setPropShowModal(true);
    const handleCloseModal = () => setPropShowModal(false);

    const tocProps = (toc) => {
        if (toc.n_records !== 0) {
            handleOpenModal();
            setTocId(toc.toc_id);
        } else {
            handleCloseModal();
        }
    };

    // 데이터 로드
    useEffect(() => {
        setIsLoading(true);
        if (data !== null) {
            console.log("Data loaded:", data);
            console.log("Tocs:", data.tocs);
            const initialData = data.tocs?.slice(0, limit) || [];
            console.log("Initial filtered data:", initialData);
            setFilteredDatas(initialData);
            index.current = limit;
            setIsLoading(false);

            if (data.tocs?.length <= limit) {
                setHasNextPage(false);
            }

            // 모든 tocs의 n_records가 0인지 확인
            if (data.tocs && data.tocs.length > 0) {
                const allZeroRecords = data.tocs.every(toc => toc.n_records === 0);
                setIsTocs(allZeroRecords);
            }

            BiographyBookMainData.forEach((item) => {
                if (item.book_id === parseInt(book_id)) {
                    setImgItem(item.image);
                }
            });
        }
        setIsLoading(false);
    }, [data, book_id]);

    // 관찰자 설정
    useEffect(() => {
        const onIntersection = (entries) => {
            const target = entries[0];
            if (target.isIntersecting && hasNextPage && !isLoading) {
                getMoreData();
            }
        };

        const observer = new IntersectionObserver(onIntersection, {
            root: null,
            rootMargin: '100px',  // 뷰포트 아래 100px 지점에서 미리 로드 시작
            threshold: 0.5  // 10%만 보여도 로드 시작
        });

        const currentObserver = observerRef.current;

        if (currentObserver) {
            observer.observe(currentObserver);
        }

        return () => {
            if (currentObserver) {
                observer.unobserve(currentObserver);
            }
        };
    }, [hasNextPage, isLoading, data]);

    // 추가 데이터 가져오기
    const getMoreData = () => {
        if (!hasNextPage || !data?.tocs || isLoading) return;

        setIsLoading(true);
        const startIndex = index.current;
        const endIndex = startIndex + limit;
        const moreData = data.tocs.slice(startIndex, endIndex);

        setTimeout(() => {
            setFilteredDatas(prevData => [...prevData, ...moreData]);
            index.current = endIndex;

            if (data.tocs.length <= endIndex) {
                setHasNextPage(false);
            }
            setIsLoading(false);
        }, 300);
    };

    const handleGoBack = () => {
        navigate(-1); // 이전 페이지로 돌아가기
    };

    return (
        <>
            <main id="contents">
                <div className="sub-content">
                    <div className="archive-wrap">
                        <div className="cont-top">
                            <div className="w-set">
                                <div className="title-wrap">
                                    <h2 className="title batang">회고록</h2>
                                    <p className="text">과거와 현재를 이어주는 회고록을 들여다보며,<br/>우리네의 삶 속에서 겪었던 다양한 교육적 경험을 확인할 수
                                        있습니다.</p>
                                </div>
                            </div>
                        </div>

                        <div className="cont-btm">
                            <div className="archive-text-box">
                                <div className="w-set">
                                    <div className="text-wrap">
                                        <p className="list-name">
                                            <span>{data.book_info?.authors}</span>
                                            <span>{data.book_info?.book_title}</span>
                                            <span>{data.book_info?.numeration}</span>
                                            <span>{data.book_info?.publisher}</span>
                                            <span>{data.book_info?.publish_year}</span>
                                            <span>{data.book_info?.library}</span>
                                        </p>
                                    </div>
                                    {imgItem &&
                                        <div className="img-wrap">
                                            <div className="archive-slides">
                                                <ul className="slides-wrapper">
                                                    <li>
                                                        <div className="imgbox"><img
                                                            src={`/images/biography/` + imgItem}
                                                            alt={data.book_info?.book_title}/></div>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                            <div className="list-wrap">
                                <div className="w-set">
                                    <div className="list-btm">
                                        <div className="table-wrap">
                                            {data ? (
                                                <>
                                                    <div className="table-top">
                                                        <strong>목차</strong>
                                                        <div className="back-list">
                                                            <div className="back" onClick={handleGoBack}>뒤로가기</div>
                                                        </div>
                                                        {isTocs && (
                                                            <div className="note-text">&#8251; 본 도서는 목차 정보만 제공하고
                                                                있습니다.</div>
                                                        )}
                                                    </div>
                                                    {filteredDatas.length > 0 ? (
                                                        <div className="scroll-table">
                                                            {filteredDatas.map((toc) => (
                                                                <>
                                                                    {toc.toc_title && (
                                                                        <div className="list-row" key={toc.toc_id}>
                                                                            <div
                                                                                className={"row-link" + (toc.n_records === 0 ? ' disabled' : '')}
                                                                                onClick={() => tocProps(toc)}>
                                                                                <div className="page">
                                                                                    <span>페이지</span> {(toc.page_start === null ? '-' : toc.page_start)}
                                                                                </div>
                                                                                <div
                                                                                    className="title bold">{toc.toc_title}</div>
                                                                            </div>
                                                                            <div
                                                                                ref={observerRef}
                                                                                style={{
                                                                                    height: '10px',
                                                                                    marginTop: '10px'
                                                                                }}
                                                                            ></div>
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ))}
                                                        </div>

                                                    ) : (
                                                        <BiographyEmpty/>
                                                    )}

                                                    {isLoading && <div></div>}
                                                    <div
                                                        ref={observerRef}
                                                        style={{ height: '10px', marginTop: '10px' }}
                                                    />
                                                </>
                                            ) : <BiographyEmpty/>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            {propShowModal &&
                <BiographyViewPopup propShowModal={propShowModal} handleCloseModal={handleCloseModal} tocId={tocId}/>}

        </>
    );
};

export default BiographyId;